import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { resetPassword } from '../../services/userService';
import { trackEvent, EventCategory, EventAction } from '../../services/analytics';
import { supabase } from '../../services/supabase';

interface LoginPageProps {
  onLogin: (email: string, password: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const [resetLoading, setResetLoading] = useState(false);
  
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handleResetDialogOpen = () => {
    setResetEmail(email); // 현재 입력된 이메일로 초기화
    setResetDialogOpen(true);
  };
  
  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
  };
  
  // 이메일 형식 검증 함수
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const handleResetPassword = async () => {
    // 이메일 형식 검증
    if (!resetEmail || !validateEmail(resetEmail)) {
      setSnackbar({
        open: true,
        message: '유효한 이메일 형식을 입력해주세요.',
        severity: 'error'
      });
      return;
    }
    
    setResetLoading(true);
    
    try {
      // 현재 세션이 있는지 확인하고 있으면 로그아웃
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session) {
        console.log('비밀번호 재설정 전 기존 세션 로그아웃');
        await supabase.auth.signOut();
      }
      
      // users 테이블에서 이메일 존재 여부 확인
      const { checkEmailExists } = await import('../../services/userService');
      const emailExists = await checkEmailExists(resetEmail);
      
      if (!emailExists) {
        setSnackbar({
          open: true,
          message: '등록되지 않은 이메일입니다. 가입된 이메일을 입력해주세요.',
          severity: 'error'
        });
        return;
      }
      
      // 비밀번호 재설정 메일 발송
      await resetPassword(resetEmail);
      
      // 비밀번호 재설정 이벤트 추적
      trackEvent(EventCategory.AUTH, EventAction.PASSWORD_RESET, resetEmail);
      setResetDialogOpen(false);
      setSnackbar({
        open: true,
        message: '비밀번호 재설정 메일이 발송되었습니다. 이메일을 확인해주세요.',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `비밀번호 재설정 메일 발송 실패: ${error.message || '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 로그인 시도 시 이벤트 추적은 실제 로그인 성공 후 App.tsx에서 수행됨
    onLogin(email, password);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'white'
      }}
    >
      <Box sx={{ transform: 'translateY(-64px)' }}>
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 5
          }}
        >
          <img 
            src="https://kconnect.ksmartech.com/asset/images/logo_01.png"
            alt="Ksmartech Logo" 
            style={{ 
              width: '282px',
              height: '46px',
              marginBottom: '16px'
            }} 
          />
        </Box>

        <Paper 
          elevation={0}
          sx={{ 
            width: '313px',
            p: 0,
            bgcolor: 'transparent'
          }}
        >
        <Box 
          component="form" 
          onSubmit={handleSubmit}
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <TextField
            fullWidth
            placeholder="이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '38px',
                '& fieldset': {
                  borderColor: '#dadce0',
                },
                '&:hover fieldset': {
                  borderColor: '#dadce0',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                height: '22px'
              }
            }}
          />
          
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            placeholder="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            InputProps={{
              endAdornment: password ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="small"
                    sx={{ color: '#5f6368', marginRight: '-10px' }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '38px',
                '& fieldset': {
                  borderColor: '#dadce0',
                },
                '&:hover fieldset': {
                  borderColor: '#dadce0',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                height: '22px'
              }
            }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0}}>
            <Button
              variant="text"
              size="small"
              onClick={handleResetDialogOpen}
              sx={{
                color: '#4a5ba7',
                textTransform: 'none',
                fontSize: '13px',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                }
              }}
            >
              비밀번호 재설정
            </Button>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 0,
              height: '48px',
              bgcolor: '#4a5ba7',
              borderRadius: '24px',
              '&:hover': {
                bgcolor: '#394584',
              },
            }}
          >
            로그인
          </Button>
        </Box>
      </Paper>
      </Box>
      
      {/* 비밀번호 재설정 다이얼로그 */}
      <Dialog open={resetDialogOpen} onClose={handleResetDialogClose}>
        <DialogTitle>비밀번호 재설정</DialogTitle>
        <DialogContent>
          <DialogContentText>
            비밀번호 재설정 링크를 받을 이메일 주소를 입력해주세요.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="이메일 주소"
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleResetDialogClose} 
            sx={{ color: '#5f6368' }}
            disabled={resetLoading}
          >
            취소
          </Button>
          <Button 
            onClick={handleResetPassword} 
            sx={{ color: '#4a5ba7' }}
            disabled={resetLoading}
          >
            {resetLoading ? <CircularProgress size={24} /> : '전송'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 알림 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginPage; 
